import * as Sentry from '@sentry/vue';

export type CachedToken = {
  token: string;
  expiresOn?: null | number;
};

export class TokenManager {
  public static getCachedToken(name: string): CachedToken | null {
    const cachedToken = localStorage.getItem(name);

    if (!cachedToken) {
      return null;
    }

    if (!cachedToken.includes('{')) {
      return null;
    }

    const parsedToken = JSON.parse(cachedToken);

    if (!parsedToken?.token) {
      return null;
    }

    return parsedToken;
  }

  /**
   * Extracts the expiration timestamp from a JWT token
   * @param token The JWT token string
   * @returns The expiration timestamp in milliseconds since epoch, or null if invalid
   */
  public static getExpirationFromToken(token: string): number | null {
    try {
      // Split the token into its three parts
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      // Decode the payload (middle part)
      const payload = parts[1];

      // Base64 URL decode - replace characters and add padding if needed
      const base64 = payload!.replace(/-/g, '+').replace(/_/g, '/');
      const paddedBase64 = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');

      // Decode using browser's atob function
      const decodedPayload = decodeURIComponent(escape(window.atob(paddedBase64)));
      const claims = JSON.parse(decodedPayload);

      // Extract the expiration time
      if (claims && claims.exp) {
        // JWT exp is in seconds, convert to milliseconds for JavaScript Date
        return claims.exp * 1000;
      }

      return null;
    } catch (error) {
      Sentry.logger.error('Error decoding JWT token', {
        error,
        log_source: 'TokenManager.getExpirationFromToken'
      });
      return null;
    }
  }

  public static setCachedToken({ token, name }: { token: string; name: string }): void {
    const cachedToken = {
      token,
      expiresOn: this.getExpirationFromToken(token)
    };
    localStorage.setItem(name, JSON.stringify(cachedToken));
  }

  public static clearCachedToken(name: string): void {
    localStorage.removeItem(name);
  }

  public static isCachedTokenExpired(name: string): boolean {
    const storedToken = TokenManager.getCachedToken(name);

    if (!storedToken) {
      return true;
    }

    if (!storedToken || !storedToken.expiresOn) {
      return true;
    }
    return new Date().getTime() >= storedToken.expiresOn; // Token is expired if the current date/time is past the expiry date
  }
}
